import { CURRENCY, LOCALE } from '@/constants/common';

type IFormatCurrency = {
  amount: number | string;
  locale?: LOCALE;
  currency?: CURRENCY;
  decimal?: number;
};

export const formatCurrency = ({
  amount,
  locale = LOCALE.US,
  currency = CURRENCY.USD,
  decimal = 2,
}: IFormatCurrency) => {
  return Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: decimal,
  }).format(Number(amount));
};

const formatNumber = (value: string) => {
  // format number 1000000 to 1,234,567
  return value.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const formatCurrencyWithIconInput = (value: string, blur?: boolean) => {
  if (value === '') {
    return '';
  }

  // check for decimal
  if (value.indexOf('.') >= 0) {
    // get position of first decimal
    // this prevents multiple decimals from
    // being entered
    const decimalPosition = value.indexOf('.');

    // split number by decimal point
    let leftSide = value.substring(0, decimalPosition);
    let rightSide = value.substring(decimalPosition);

    // add commas to left side of number
    leftSide = formatNumber(leftSide);

    // validate right side
    rightSide = formatNumber(rightSide);

    // On blur make sure 2 numbers after decimal
    if (blur) {
      rightSide += '00';
    }

    // Limit decimal to only 2 digits
    rightSide = rightSide.substring(0, 2);

    // join number by .
    value = leftSide + '.' + rightSide;
  } else {
    // no decimal entered
    // add commas to number
    // remove all non-digits
    value = formatNumber(value);

    // final formatting
    if (blur) {
      value += '.00';
    }
  }

  return value;
};

/**
 * Convert a number string to number
 * Example: 12,345.00 -> 12345
 * 12 345 -> 12345
 */
export const convertToNumber = (value: string): number =>
  Number(value.replace(/[,\s]/g, ''));

export const abbreviateAmount = (amount: number) => {
  const SI_SYMBOL = ['', 'k', 'M', 'G', 'T', 'P', 'E'];
  // Convert the number to an absolute value
  const absoluteNumber = Math.abs(amount);

  // Determine the appropriate SI symbol (k, M, G, etc.)
  const tier = Math.min(
    (Math.log10(absoluteNumber) / 3) | 0,
    SI_SYMBOL.length - 1,
  );
  let suffix = SI_SYMBOL[tier];

  // Calculate the abbreviated number
  let abbreviatedNumber: string | number;

  if (
    tier > 0 &&
    absoluteNumber >= Math.pow(10, tier * 3) &&
    absoluteNumber < Math.pow(10, (tier + 1) * 3)
  ) {
    abbreviatedNumber = (amount / Math.pow(10, tier * 3)).toFixed(0);
    if (abbreviatedNumber.endsWith('.0')) {
      abbreviatedNumber = Number(abbreviatedNumber).toFixed(0);
      if (abbreviatedNumber.length > 3) {
        abbreviatedNumber = Math.floor(+abbreviatedNumber / 1000);
        suffix = SI_SYMBOL[tier + 1];
      }
    }
  } else {
    abbreviatedNumber = Math.floor(amount / Math.pow(10, tier * 3));
  }

  return `${abbreviatedNumber}${suffix}`;
};
